import type { RoutingMode, TelephonyRoutingReadiness, VoiceProvider } from '@callhub/shared';

function formatRoutingModeLabel(mode: RoutingMode): string {
  return mode === 'provider_router' ? 'New routing (provider router)' : 'Twilio (legacy)';
}

type RoutingReadinessStripProps = {
  readiness: TelephonyRoutingReadiness;
  title?: string;
  className?: string;
  statuses?: ReadinessStatusBadge[];
};

const PROVIDER_ORDER: VoiceProvider[] = ['telnyx', 'twilio', 'signalwire'];

export type ReadinessStatusTone = 'good' | 'watch' | 'critical' | 'neutral';

export type ReadinessStatusBadge = {
  label: string;
  value: string;
  tone?: ReadinessStatusTone;
};

const STATUS_TONE_CLASSES: Record<ReadinessStatusTone, string> = {
  good: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  watch: 'bg-amber-500/20 text-amber-700 dark:text-amber-300',
  critical: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  neutral: 'bg-background/70 text-muted-foreground',
};

function resolveStatusToneClass(tone: ReadinessStatusTone): string {
  return STATUS_TONE_CLASSES[tone];
}

export function RoutingReadinessStrip({
  readiness,
  title = 'Routing readiness',
  className = '',
  statuses = [],
}: RoutingReadinessStripProps) {
  return (
    <div className={`rounded-xl border border-border bg-background/70 p-4 ${className}`.trim()}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            readiness.recommendedRoutingMode === 'provider_router'
              ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
              : 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
          }`}
        >
          Recommended: {formatRoutingModeLabel(readiness.recommendedRoutingMode)}
        </span>
      </div>

      {statuses.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {statuses.map((status) => (
            <span
              key={`readiness-status-${status.label}`}
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${resolveStatusToneClass(
                status.tone ?? 'neutral',
              )}`}
            >
              {status.label}: {status.value}
            </span>
          ))}
        </div>
      ) : null}

      <div className="mt-3 grid gap-2 md:grid-cols-3">
        {PROVIDER_ORDER.map((provider) => {
          const providerReadiness = readiness.providers[provider];
          return (
            <div
              key={`routing-readiness-${provider}`}
              className="rounded-lg border border-border bg-card px-3 py-2"
            >
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="font-medium text-foreground">{provider}</span>
                <span
                  className={
                    providerReadiness.ready
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-amber-700 dark:text-amber-300'
                  }
                >
                  {providerReadiness.ready ? 'Ready' : 'Blocked'}
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{providerReadiness.reason}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-3">
        <p className="text-xs font-medium text-foreground">Cutover blockers</p>
        {readiness.blockingIssues.length === 0 ? (
          <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
            No blocking issues detected for the current routing policy.
          </p>
        ) : (
          <ul className="mt-1 space-y-1 text-xs text-amber-700 dark:text-amber-300">
            {readiness.blockingIssues.map((issue, index) => (
              <li key={`cutover-blocker-${index}`}>• {issue}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
